import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/utils/apiClient';

interface Job {
  id: string;
  status: string;
  job_type: string;
  progress: number;
  created_at: string;
  completed_at?: string;
  result_data?: any;
  error_message?: string;
}

interface PendingJobsData {
  dlqJobs: Job[];
  recentlyCompleted: Job[];
  totalPending: number;
}

export function usePendingJobs() {
  const { user } = useAuth();

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['pendingJobs', user?.id],
    queryFn: async (): Promise<PendingJobsData> => {
      const isAuthenticated = localStorage.getItem('is_authenticated');

      if (isAuthenticated !== 'true') {
        return { dlqJobs: [], recentlyCompleted: [], totalPending: 0 };
      }

      // Fetch both in parallel
      const [dlqResponse, completedResponse] = await Promise.all([
        apiClient.get('/jobs/dlq'),
        apiClient.get('/jobs?status=completed&limit=5')
      ]);

      const dlqData = Array.isArray(dlqResponse) ? dlqResponse : [];
      const completedData = Array.isArray(completedResponse) ? completedResponse : [];

      // Filter recently completed (last 15 minutes)
      const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000);
      const recentlyCompleted = completedData.filter((job: Job) => {
        if (!job.completed_at) return false;
        return new Date(job.completed_at) > fifteenMinutesAgo;
      });

      return {
        dlqJobs: dlqData,
        recentlyCompleted,
        totalPending: dlqData.length
      };
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Poll every 60 seconds when window is focused
    refetchIntervalInBackground: false, // Don't poll when tab is not visible
    refetchOnWindowFocus: true,
  });

  return {
    dlqJobs: data?.dlqJobs ?? [],
    recentlyCompleted: data?.recentlyCompleted ?? [],
    totalPending: data?.totalPending ?? 0,
    isLoading,
    error: error ? 'Failed to load notifications' : null,
    refresh: refetch,
  };
}
